/**
 * @packageDocumentation
 * @module      CorThemeSelector.ts
 * @description Encapsulates the persistence of the user's theme choice.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorThemeSelector_1;
import { LitElement, html, customElement, internalProperty } from 'lit-element';
/** Enumerates the available themes. These choices are defined in Shui. */
export var ThemeName;
(function (ThemeName) {
    ThemeName["Light"] = "light";
    ThemeName["Dark"] = "dark";
})(ThemeName || (ThemeName = {}));
/**
 * A component by which the user may choose a visual theme.
 *
 * This class inserts the chosen theme as a theme attribute in the html tag of the document.
 * Shui's CSS selectors (shui-styles.css) are then based on html[theme="light"] or html[theme="dark"]
 */
let CorThemeSelector = CorThemeSelector_1 = class CorThemeSelector extends LitElement {
    /** Set the theme in the HTML document and also in local storage.
     * During the first-time theme setting, the theme attribute would be null.
     * Only for the first time, we fetch from local storage because other windows/tabs might have modified the theme.
    */
    static setTheme(themeName) {
        const htmlElement = document.getElementsByTagName("html")[0];
        let themeAttr = document.createAttribute("theme");
        themeAttr.value = themeName;
        htmlElement.attributes.setNamedItem(themeAttr);
        if (localStorage.getItem("theme") != themeName) {
            localStorage.setItem("theme", themeName);
        }
    }
    static getTheme() {
        var _a;
        const htmlElement = document.getElementsByTagName("html")[0];
        return (((_a = htmlElement.attributes.getNamedItem("theme")) === null || _a === void 0 ? void 0 : _a.value) == 'dark') ? ThemeName.Dark : ThemeName.Light;
    }
    /** Clear the theme, thus returning to the default (Light) theme. */
    static clearTheme() {
        document.getElementsByTagName('html')[0].removeAttribute("theme");
    }
    /** Tell whether the theme is dark (true) or light (false). */
    get isDark() {
        return CorThemeSelector_1.getTheme() == ThemeName.Dark;
    }
    /** Toggle the theme between dark and light. */
    toggleTheme() {
        this.isDark ? CorThemeSelector_1.setTheme(ThemeName.Light) : CorThemeSelector_1.setTheme(ThemeName.Dark);
    }
    static initializeTheme() {
        CorThemeSelector_1.setTheme(localStorage.getItem("theme"));
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        return html `<sh-toggle label='Dark theme' ?active=${this.isDark} @click=${this.toggleTheme}></sh-toggle>`;
    }
};
__decorate([
    internalProperty()
], CorThemeSelector.prototype, "isDark", null);
CorThemeSelector = CorThemeSelector_1 = __decorate([
    customElement('cor-theme-selector')
], CorThemeSelector);
export default CorThemeSelector;
//# sourceMappingURL=CorThemeSelector.js.map